import { UserOutlined } from '@ant-design/icons';
import React, { FC, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { setHeader, setRoutes } from 'src/core/ui/shell';
import { AppConfig, container } from 'src/inversify.config';
import { UserSummary, UserSummaryStore } from 'src/stores/users-store';
import { CustomFilterField, TableModel, TableView } from 'src/core/ui/collections/table';
import { Button, Card, Modal, Row, Tag } from 'antd';
import { ListState, Query } from 'src/core/stores/data-store';
import { formatMessage } from 'src/core/services/http.service';
import NewUserDialog from './user-new';
import { AutocompleteTextFilter } from 'src/common/components/table-filters/autocomplete-text-filter';
import { ENV } from 'src/';
import { emptyTableModel } from 'src/common/utils';
import { AppConfiguration } from 'src/core/services/configuration.service';
import CsvReport from '../licenses/reports/csv-report';
import { OptionsFilterMultiple } from 'src/common/components/table-filters/options-filter-multiple';
import { OrganizationSummaryStore } from 'src/stores/organizations-store';

const UsersListPage: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const config: AppConfiguration = useContext(AppConfig);
  const [query, setQuery] = useState({
    searchQuery: '',
    orderBy: [{ field: 'email', direction: 'Ascending', useProfile: false }],
    skip: 0,
    take: 10,
  } as Query);

  const usersStore = useMemo(() => container.get(UserSummaryStore), []);
  const usersState = usersStore.state;
  const [newUserVisible, setNewUserVisible] = useState(false);
  const [exportDialogVisible, setExportDialogVisible] = useState(false);
  const [selectedCsvSeparator, setSelectedCsvSeparator] = useState(',');
  const [isExporting, setIsExporting] = useState(false);
  const [organizationFilterMode, setOrganizationFilterMode] = useState<'Any' | 'All'>('Any');
  const organizationsStore = container.get(OrganizationSummaryStore);
  const organizationsState = organizationsStore.state;
  const organizationsQuery = {
    searchQuery: '',
    orderBy: [{ field: 'displayName', direction: 'Ascending', useProfile: false }],
    skip: 0,
    take: 10000,
  } as Query;

  setHeader(t('Users'));
  setRoutes([
    { path: '', breadcrumbName: t('Home') },
    { path: 'users', breadcrumbName: t('Users') },
  ]);

  useEffect(
    () => {
      const load = async () => {
        await usersStore.load(query);
        await organizationsStore.load(organizationsQuery);
      };
      load();
    },
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const tableModel = {
    query,
    columns: [
      {
        sortable: true,
        searchable: true,
        field: 'email',
        title: t('Email'),
        applyPartialFilter: true,
        filter: <AutocompleteTextFilter baseUrl={usersStore.baseUrl} fieldName={'Email'} fieldValue={'email'} />,
        fiterField: 'Email',
        renderer: (data: UserSummary) => (
          <span>
            <Link to={`${data.id}`}>
              <UserOutlined />
              &nbsp;<span>{data.email}</span>
            </Link>
          </span>
        ),
      },
      {
        searchable: true,
        sortable: true,
        sortField: 'DisplayName',
        useProfile: true,
        field: 'DisplayName',
        title: t('DisplayName'),
        renderer: (data: UserSummary) => <span>{data.displayName}</span>,
      },
      {
        queryFilterMode: CustomFilterField.SearchInCollection,
        field: 'Organizations/OrganizationId',
        title: t('Organization'),
        multipleFilterMode: organizationFilterMode,
        filter: (
          <OptionsFilterMultiple
            allowChangeFilterMode
            changeFilterMode={setOrganizationFilterMode}
            options={organizationsState.value.items.map((v) => ({ value: v.id, label: v.displayName }))}
          />
        ),
        renderer: (data) => (
          <span>
            {data.organizations.map((org) => (
              <Tag key={org.organizationDisplayName}>{org.organizationDisplayName}</Tag>
            ))}
          </span>
        ),
      },
    ],
    sortFields: [],
    data: usersState.value as ListState<UserSummary>,
  } as TableModel<UserSummary>;

  return (
    <Row align="top" justify="space-between">
      <Card style={{ width: '100%' }}>
        <TableView
          rowKey="id"
          canCreateNew={false}
          onNewItem={() => setNewUserVisible(true)}
          newItemName={t('AddUser')}
          canDelete={false}
          onQueryChanged={(query: Query) => {
            setQuery(query);
            usersStore.load(query);
          }}
          onRefresh={() => usersStore.load(query)}
          model={usersState.errorMessage.value ? emptyTableModel : tableModel}
          error={
            usersState.errorMessage.value && (config.environment === ENV.DEV ? formatMessage(usersState.errorMessage.value) : t('UsersTableError'))
          }
          rightToolbar={
            <>
              <li>
                <Button
                  type="primary"
                  onClick={() => {
                    setExportDialogVisible(true);
                  }}
                >
                  {t('ExportCSV')}
                </Button>
              </li>
            </>
          }
        />
        {newUserVisible && (
          <NewUserDialog
            onClose={(response) => {
              setNewUserVisible(false);
              if (response) {
                navigate(`${response.identity}`);
              }
            }}
          />
        )}

        <Modal
          title={<h3>{`${t('Reports')}`}</h3>}
          okText={t('Export')}
          cancelText={t('Cancel')}
          cancelButtonProps={{ type: 'default' }}
          open={exportDialogVisible}
          okButtonProps={{ loading: isExporting }}
          onCancel={() => {
            setExportDialogVisible(false);
          }}
          onOk={async () => {
            setIsExporting(true);
            const csvQuery = { ...query, take: 100000, skip: 0 };
            await usersStore.exportCsv(csvQuery, selectedCsvSeparator, 'users-report.csv');
            setIsExporting(false);
            setExportDialogVisible(false);
          }}
        >
          <CsvReport
            onChange={(item) => {
              setSelectedCsvSeparator(item);
            }}
          />
        </Modal>
      </Card>
    </Row>
  );
};

export default UsersListPage;
